/**
 * SN-8310 — the payment milestones' geometry, as CSS lengths.
 *
 * Dots sit on a line inset by half a dot at each end, so a mark at 0% or 100%
 * is not clipped: its centre is `12px + (100% − 24px) × position`.
 *
 * The labels are a GRID under the line, not absolutely placed boxes. Each
 * column edge falls halfway between two neighbouring dots, so a label centred
 * in its column is centred on its dot, the first label starts at its dot's left
 * edge and the last ends at its dot's right edge. Being a grid, the row grows
 * with the text: a label that wraps can never spill out of the header, which is
 * what a fixed-height box of absolute labels did on real data.
 */
export const MILESTONE_DOT = 24

const HALF = MILESTONE_DOT / 2

/** A fraction to four places, so the CSS stays short and stable. */
const fraction = (percent: number): number => Number((percent / 100).toFixed(4))

const span = (percent: number): string =>
  `(100% - ${MILESTONE_DOT}px) * ${fraction(percent)}`

/** The horizontal offset of a point on the track, 0–100 along it. */
export const trackOffset = (position: number): string =>
  `calc(${HALF}px + ${span(position)})`

/** The width from the track's start to a point on it (the elapsed fill). */
export const trackLength = (position: number): string =>
  `calc(${span(position)})`

/**
 * `grid-template-columns` for the labels under marks at `positions`.
 *
 * With Today past the last mark, the marks use only part of the track (the rest
 * is the tail), so the last label's column ends at the last dot and an empty
 * filler column takes the tail.
 */
export const milestoneColumns = (
  positions: number[],
  pastLast: boolean
): string => {
  const count = positions.length
  // A lone mark sits at the start: its label simply reads left to right.
  if (count <= 1) {
    return '1fr'
  }
  const edges = positions
    .slice(0, -1)
    .map((position, i) => (position + positions[i + 1]) / 2)

  const columns = [`calc(${HALF}px + ${span(edges[0])})`]
  for (let i = 1; i < count - 1; i++) {
    columns.push(`calc(${span(edges[i] - edges[i - 1])})`)
  }
  if (pastLast) {
    columns.push(
      `calc(${HALF}px + ${span(positions[count - 1] - edges[count - 2])})`,
      '1fr'
    )
  } else {
    columns.push('1fr')
  }

  return columns.join(' ')
}
